import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import API from "../../config/config";
import { toast } from "react-toastify";

const StepChoices = forwardRef(
  ({ formData, setFormData, driver, disabled, refreshDriver }, ref) => {
    const [loading, setLoading] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [errors, setErrors] = useState({});
    const token = localStorage.getItem("adminToken"); // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (driver && driver.drivingChoices) {
        const drivingChoices = driver.drivingChoices;
        const filledData = {
          tripType: formData.tripType || drivingChoices.tripType || "",
          carTransmission:
            formData.carTransmission || drivingChoices.carTransmission || "",
          carTypes:
            formData.carTypes && formData.carTypes.length > 0
              ? formData.carTypes
              : Array.isArray(drivingChoices.carTypes)
              ? drivingChoices.carTypes
              : Array.isArray(drivingChoices.carType)
              ? drivingChoices.carType
              : drivingChoices.carTypes || drivingChoices.carType
              ? [drivingChoices.carTypes || drivingChoices.carType]
              : [],
          city: formData.city || drivingChoices.city || "",
          area: formData.area || drivingChoices.area || "",
        };

        setFormData((prev) => ({ ...prev, ...filledData }));
        setInitialData(filledData);
      } else if (driver && !driver.drivingChoices) {
        // Initialize with empty data if no driving choices exist
        const emptyData = {
          tripType: formData.tripType || "",
          carTransmission: formData.carTransmission || "",
          carTypes: formData.carTypes || [],
          city: formData.city || "",
          area: formData.area || "",
        };
        setFormData((prev) => ({ ...prev, ...emptyData }));
        setInitialData(emptyData);
      }
    }, [driver]);

    const handleChange = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user makes a selection
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    // Real-time validation function
    const validateField = (field, value) => {
      const fieldLabels = {
        tripType: "Trip Type",
        carTransmission: "Car Transmission",
        carTypes: "Car Types",
        city: "City",
        area: "Area",
      };

      if (!value || (Array.isArray(value) && value.length === 0)) {
        setErrors((prev) => ({
          ...prev,
          [field]: `Please select/enter ${fieldLabels[field]}.`,
        }));
        return false;
      }

      setErrors((prev) => ({ ...prev, [field]: "" }));
      return true;
    };

    // Validate all fields
    const validateAllFields = () => {
      const requiredFields = [
        "tripType",
        "carTransmission",
        "carTypes",
        "city",
        "area",
      ];
      let hasErrors = false;

      requiredFields.forEach((field) => {
        const isValid = validateField(field, formData[field]);
        if (!isValid) hasErrors = true;
      });

      return !hasErrors;
    };
    const toggleCarType = (type) => {
      let selected = formData.carTypes || [];
      const coreTypes = ["Hatchback", "Sedan", "SUV", "Luxury"];

      if (type === "All") {
        // Toggle "All" - if currently selected, deselect everything; if not, select everything
        const isAllSelected = selected.includes("All");
        selected = isAllSelected ? [] : [...coreTypes, "All"];
      } else {
        // Toggle individual type - always allow individual selection/deselection
        if (selected.includes(type)) {
          // Remove the type but keep other selections including "All"
          selected = selected.filter((t) => t !== type);
          // If "All" was selected but now not all core types are selected, remove "All"
          if (
            selected.includes("All") &&
            !coreTypes.every((t) => selected.includes(t))
          ) {
            selected = selected.filter((t) => t !== "All");
          }
        } else {
          // Add the type to existing selections
          selected = [...selected, type];
          // If all core types are now selected and "All" isn't already there, add "All"
          if (
            coreTypes.every((t) => selected.includes(t)) &&
            !selected.includes("All")
          ) {
            selected = [...selected, "All"];
          }
        }
      }

      setFormData((prev) => ({ ...prev, carTypes: selected }));
    };

    const isDataChanged = () => {
      if (!initialData) return true;

      const normalize = (arr) =>
        [...arr]
          .filter((v) => v !== "All")
          .sort()
          .join(",");

      return (
        formData.tripType !== initialData.tripType ||
        formData.carTransmission !== initialData.carTransmission ||
        formData.city !== initialData.city ||
        formData.area !== initialData.area ||
        normalize(formData.carTypes || []) !==
          normalize(initialData.carTypes || [])
      );
    };

    useImperativeHandle(ref, () => ({
      validateAndSubmit: async () => {
        // Validate all fields and show errors
        if (!validateAllFields()) {
          toast.error("Please complete all required fields.");
          throw new Error("Please complete all required fields.");
        }

        if (!isDataChanged()) {
          console.log("No changes detected");
          return "unchanged";
        }

        setLoading(true);
        try {
          let finalCarTypes = [...(formData.carTypes || [])];

          // Safety check
          if (!finalCarTypes || finalCarTypes.length === 0) {
            toast.error("Please select at least one car type.");
            throw new Error("Please select at least one car type.");
          }

          if (finalCarTypes.includes("All")) {
            finalCarTypes = ["Hatchback", "Sedan", "SUV", "Luxury"];
          }

          // Convert car types to lowercase as per API requirement
          const carTypeLowercase = finalCarTypes.map((type) => type.toLowerCase());

          const payload = {
            ownerId: driver._id,
            tripType: formData.tripType,
            carTransmission: formData.carTransmission,
            carType: carTypeLowercase,
            city: formData.city,
            area: formData.area || "",
          };

          console.log("Saving driving choices payload:", payload);

          const res = await fetch(API.ADMIN.SAVE_ADMIN_DRIVING_CHOICES, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          const responseData = await res.json();
          console.log("API response:", responseData);

          if (!res.ok) {
            throw new Error(responseData.message || "Update failed");
          }

          toast.success("Driving choices saved successfully!");

          // Refresh driver data to update stepper status
          if (refreshDriver) {
            console.log("Refreshing driver data...");
            await refreshDriver();
            console.log("Driver data refreshed");
          }

          // Update initial data to reflect saved state
          setInitialData({
            tripType: formData.tripType,
            carTransmission: formData.carTransmission,
            carTypes: finalCarTypes,
            city: formData.city,
            area: formData.area || "",
          });

          console.log("Driving choices save completed successfully");
          return true;
        } catch (err) {
          console.error("Driving choices error:", err);
          const errorMessage =
            err.message || "Failed to save driving preferences";
          toast.error(errorMessage);
          throw new Error("❌ " + errorMessage);
        } finally {
          setLoading(false);
        }
      },
    }));

    const carTypeOptions = ["Hatchback", "Sedan", "SUV", "Luxury", "All"];

    return (
      <div className="max-w-4xl mx-auto p-4 bg-white rounded shadow text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Trip Type */}
          <div>
            <label className="block mb-1 font-medium text-sm">
              Trip Type <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {["One-Way", "Round-Trip", "Both"].map((value) => (
                <label
                  key={value}
                  className="flex items-center gap-1 capitalize"
                >
                  <input
                    type="radio"
                    name="tripType"
                    value={value}
                    checked={formData.tripType === value}
                    onChange={() => handleChange("tripType", value)}
                    onBlur={() => validateField("tripType", formData.tripType)}
                    disabled={loading}
                  />
                  {value.replace("_", " ")}
                </label>
              ))}
            </div>
            {errors.tripType && (
              <p className="text-red-500 text-xs mt-1">{errors.tripType}</p>
            )}
          </div>

          {/* Car Transmission */}
          <div>
            <label className="block mb-1 font-medium text-sm">
              Car Transmission <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {["Manual", "Manual and Automatic"].map((value) => (
                <label
                  key={value}
                  className="flex items-center gap-1 capitalize"
                >
                  <input
                    type="radio"
                    name="carTransmission"
                    value={value}
                    checked={formData.carTransmission === value}
                    onChange={() => handleChange("carTransmission", value)}
                    onBlur={() =>
                      validateField("carTransmission", formData.carTransmission)
                    }
                    disabled={loading}
                  />
                  {value}
                </label>
              ))}
            </div>
            {errors.carTransmission && (
              <p className="text-red-500 text-xs mt-1">
                {errors.carTransmission}
              </p>
            )}
          </div>

          {/* Car Types */}
          <div>
            <label className="block mb-1 font-medium text-sm">
              Car Types <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {carTypeOptions.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={(formData.carTypes || []).includes(type)}
                    onChange={() => {
                      toggleCarType(type);
                      // Validate after change
                      setTimeout(
                        () => validateField("carTypes", formData.carTypes),
                        0
                      );
                    }}
                    disabled={loading}
                  />
                  {type}
                </label>
              ))}
            </div>
            {errors.carTypes && (
              <p className="text-red-500 text-xs mt-1">{errors.carTypes}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label className="block mb-1 font-medium text-sm">
              City to Drive In <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full border rounded px-3 py-1 text-sm ${
                errors.city ? "border-red-500" : "border-gray-300"
              }`}
              value={formData.city || ""}
              onChange={(e) => handleChange("city", e.target.value)}
              onBlur={() => validateField("city", formData.city)}
              required
              disabled={loading}
            >
              <option value="">Select City</option>
              <option value="Hyderabad">Hyderabad</option>
              <option value="Bangalore">Bangalore</option>
              <option value="Delhi">Delhi</option>
              <option value="Mumbai">Mumbai</option>
              <option value="Chennai">Chennai</option>
              <option value="Pune">Pune</option>
              <option value="Ahmedabad">Ahmedabad</option>
              <option value="Kolkata">Kolkata</option>
              <option value="Surat">Surat</option>
              <option value="Jaipur">Jaipur</option>
            </select>
            {errors.city && (
              <p className="text-red-500 text-xs mt-1">{errors.city}</p>
            )}
          </div>

          {/* Area */}
          <div>
            <label className="block mb-1 font-medium text-sm">
              Area <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`w-full border rounded px-3 py-1 text-sm ${
                errors.area ? "border-red-500" : "border-gray-300"
              }`}
              value={formData.area || ""}
              onChange={(e) => handleChange("area", e.target.value)}
              onBlur={() => validateField("area", formData.area)}
              disabled={loading}
              required
              placeholder="Enter your area"
            />
            {errors.area && (
              <p className="text-red-500 text-xs mt-1">{errors.area}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default StepChoices;
